import React from "react"
import {
  Col,
  Divider,
  Drawer,
  Form,
  Input,
  Row,
  Select,
  notification,
} from "antd"
import { Button } from "src/components/actions/button"
import { CloseSvg, SaveSvg } from "src/components/Svg"
import { useAppData } from "src/store/app-data"
import { usePermission } from "src/hooks"
import api from "src/api"
import styled from "styled-components"

export function AccessDrawer({ title, userId, onClose }) {
  const layoutConfig = useAppData((state) => state.layoutConfig)
  const { hasPermission } = usePermission()
  const canEditAccess = hasPermission("clients-access.edit")
  const canEditPassword = hasPermission("clients-password.edit")
  const [form] = Form.useForm()
  const [passwordForm] = Form.useForm()
  const [isLoading, setIsLoading] = React.useState(true)
  const [allRoles, setAllRoles] = React.useState([])
  const [allPermissions, setAllPermissions] = React.useState([])
  const [allPoints, setAllPoints] = React.useState([])

  React.useEffect(() => {
    if (userId) {
      loadAccess()
    }
  }, [userId])

  async function loadAccess() {
    setIsLoading(true)
    try {
      const { data } = await api.get(`users/${userId}/access`)
      form.setFieldsValue({
        roleIds: data.roles?.map((r) => r.id) ?? [],
        permissionIds: data.permissions?.map((p) => p.id) ?? [],
        pointIds: data.resourceAccess?.point ?? [],
      })
      setAllRoles(
        data.allRoles?.map((r) => ({
          label: `${r.name} (${r.slug})`,
          value: r.id,
        })) ?? []
      )
      setAllPermissions(
        data.allPermissions?.map((p) => ({
          label: `${p.name} (${p.slug})`,
          value: p.id,
        })) ?? []
      )
      setAllPoints(
        data.allPoints?.map((p) => ({
          label: `${p.name}${p.shortName ? ` (${p.shortName})` : ""}`,
          value: p.id,
        })) ?? []
      )
    } catch (error) {
      console.error("Ошибка загрузки доступа", error)
      notification.error({ message: "Ошибка загрузки данных доступа" })
    } finally {
      setIsLoading(false)
    }
  }

  async function onSubmitAccess(values) {
    try {
      const payload = {
        roleIds: values.roleIds ?? [],
        permissionIds: values.permissionIds ?? [],
        resourceAccess: [
          { type: "point", ids: values.pointIds ?? [] },
        ],
      }
      await api.put(`users/${userId}/access`, payload)
      notification.success({ message: "Доступ обновлён" })
    } catch (error) {
      console.error("Ошибка обновления доступа", error)
      notification.error({ message: "Ошибка обновления доступа" })
    }
  }

  async function onSubmitPassword(values) {
    try {
      await api.post(`users/${userId}/password`, {
        password: values.password,
      })
      notification.success({ message: "Пароль обновлён" })
      passwordForm.resetFields()
    } catch (error) {
      console.error("Ошибка обновления пароля", error)
      notification.error({ message: "Ошибка обновления пароля" })
    }
  }

  function handleClose() {
    onClose()
    form.resetFields()
    passwordForm.resetFields()
  }

  return (
    <DrawerEdit
      width={layoutConfig.isMobile ? "90%" : "60%"}
      title={title}
      onClose={handleClose}
      open
      loading={isLoading}
    >
      <FormEdit
        form={form}
        layout={"vertical"}
        onFinish={onSubmitAccess}
      >
        <StyledFormItem label={"Роли"} name={"roleIds"}>
          <Select
            mode="multiple"
            allowClear
            disabled={!canEditAccess}
            placeholder={"Выберите роли"}
            options={allRoles}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </StyledFormItem>

        <StyledFormItem
          label={
            <Row justify="space-between" align="middle" style={{ width: "100%" }}>
              <span>Прямые разрешения</span>
              {canEditAccess && (
                <Button
                  size="small"
                  type="link"
                  onClick={() => {
                    const allIds = allPermissions.map((o) => o.value)
                    form.setFieldsValue({ permissionIds: allIds })
                  }}
                >
                  Выбрать все
                </Button>
              )}
            </Row>
          }
          name={"permissionIds"}
        >
          <Select
            mode="multiple"
            allowClear
            disabled={!canEditAccess}
            placeholder={"Выберите разрешения"}
            options={allPermissions}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </StyledFormItem>

        <Divider orientation="left">Ограничения по ресурсам</Divider>

        <StyledFormItem
          label={
            <Row justify="space-between" align="middle" style={{ width: "100%" }}>
              <span>Доступные точки</span>
              <span style={{ color: "#999", fontSize: 12 }}>
                Пусто = доступ ко всем
              </span>
            </Row>
          }
          name={"pointIds"}
        >
          <Select
            mode="multiple"
            allowClear
            disabled={!canEditAccess}
            placeholder={"Все точки (без ограничений)"}
            options={allPoints}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </StyledFormItem>

        <ButtonRow>
          <ButtonCol>
            <Button onClick={handleClose} icon={<CloseSvg />}>
              Закрыть
            </Button>
          </ButtonCol>
          {canEditAccess && (
            <ButtonCol>
              <Button icon={<SaveSvg />} type="primary" htmlType="submit">
                Сохранить доступ
              </Button>
            </ButtonCol>
          )}
        </ButtonRow>
      </FormEdit>

      {canEditPassword && (
        <>
          <Divider />

          <FormEdit
            form={passwordForm}
            layout={"vertical"}
            onFinish={onSubmitPassword}
          >
            <h3>Смена пароля</h3>

            <StyledFormItem
              label={"Новый пароль"}
              name={"password"}
              rules={[
                { required: true, message: "Введите пароль!" },
                { min: 6, message: "Минимум 6 символов" },
              ]}
            >
              <Input.Password placeholder={"Введите новый пароль"} />
            </StyledFormItem>

            <StyledFormItem
              label={"Подтверждение пароля"}
              name={"passwordConfirm"}
              dependencies={["password"]}
              rules={[
                { required: true, message: "Подтвердите пароль!" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error("Пароли не совпадают"))
                  },
                }),
              ]}
            >
              <Input.Password placeholder={"Повторите пароль"} />
            </StyledFormItem>

            <ButtonRow>
              <ButtonCol>
                <Button icon={<SaveSvg />} type="primary" htmlType="submit">
                  Обновить пароль
                </Button>
              </ButtonCol>
            </ButtonRow>
          </FormEdit>
        </>
      )}
    </DrawerEdit>
  )
}

const DrawerEdit = styled(Drawer)``

const FormEdit = styled(Form)``

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 16px;
`

const ButtonCol = styled(Col)`
  padding-bottom: 5px;
`

const ButtonRow = styled(Row)`
  align-items: center;
  justify-content: flex-end;
  margin-top: 10px;

  .ant-col {
    margin-left: 0.4em;
  }
`
